import {
  createContext,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { CloseCircle } from "../../Icons";
import { useDeviceTier } from "../../../hooks/useDeviceTier";
import { useLatestRef } from "../../../hooks/useLatestRef";

/**
 * Single dialog primitive. Replaces GeneralDialog + MobileDialog,
 * collapses every place in the codebase that hand-rolled a modal
 * shell. The variants:
 *
 *   - `auto` (default) — sheet on phones, centered modal on tablet
 *     and desktop. Phones keep the thumb-reachable bottom sheet; the
 *     tablet band gets a proper centered card instead of a full-bleed
 *     sheet stretched across an iPad.
 *   - `modal` — centered card with backdrop, regardless of viewport.
 *   - `sheet` — bottom-anchored full-width sheet, regardless of
 *     viewport.
 *
 * What this gets right that the old pair didn't:
 *
 *   - **Focus trap.** Tab cycles inside the dialog. The previous
 *     dialogs let focus escape into the dimmed page underneath.
 *   - **Restore focus on close.** Focus returns to whatever the
 *     user had focused before opening — accessibility table stakes
 *     that was missing.
 *   - **ESC to close.** Wired by default; opt out with
 *     `closeOnEscape={false}` for dialogs that need explicit
 *     confirmation (none today, but the prop exists).
 *   - **Body scroll lock.** Prevents the page underneath from
 *     scrolling while the dialog is open — currently the page
 *     scrolls behind every dialog.
 *   - **ARIA roles + aria-modal + labelled-by.** Screen readers
 *     now know what they're announcing.
 *   - **No `Math.random()` ids.** `useId()` for stable association
 *     between the dialog and its title.
 */

type DialogVariant = "auto" | "modal" | "sheet";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Defaults to `auto` (sheet on mobile / modal on desktop). Force
   * a specific shape with `modal` or `sheet`.
   */
  variant?: DialogVariant;
  /**
   * Optional extra class applied to the dialog surface (the inner
   * card, not the backdrop). For shape/sizing variants per call site.
   */
  className?: string;
  /** Mostly off-switch for confirm-style dialogs. Default true. */
  closeOnEscape?: boolean;
  /** Click on backdrop closes by default; set false for forms in progress. */
  closeOnBackdropClick?: boolean;
  /** Whether to render the default close (X) button in the header. */
  hideCloseButton?: boolean;
  /** The `aria-labelledby` target — wire this id to your Dialog.Header. */
  titleId?: string;
  children: ReactNode;
}

interface DialogContextValue {
  onClose: () => void;
  hideCloseButton: boolean;
  titleId: string;
}

const DialogContext = createContext<DialogContextValue | null>(null);

function useDialogContext(): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error("Dialog.* must be used inside a <Dialog>.");
  }
  return ctx;
}

/**
 * Move focus to the first interactive element inside `root`, falling
 * back to `root` itself if it can be focused.
 */
function focusFirstWithin(root: HTMLElement) {
  const focusables = root.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
  );
  const first = focusables[0];
  if (first) {
    first.focus();
  } else {
    root.focus();
  }
}

function trapTabKey(event: KeyboardEvent, root: HTMLElement) {
  if (event.key !== "Tab") return;
  const focusables = Array.from(
    root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  );
  if (focusables.length === 0) {
    event.preventDefault();
    return;
  }
  const first = focusables[0]!;
  const last = focusables[focusables.length - 1]!;
  const active = document.activeElement as HTMLElement | null;

  if (event.shiftKey) {
    if (active === first || !root.contains(active)) {
      event.preventDefault();
      last.focus();
    }
  } else {
    if (active === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

export function Dialog(props: DialogProps) {
  const {
    open,
    onClose,
    variant = "auto",
    className,
    closeOnEscape = true,
    closeOnBackdropClick = true,
    hideCloseButton = false,
    titleId: providedTitleId,
    children,
  } = props;

  const tier = useDeviceTier();
  const generatedTitleId = useId();
  const titleId = providedTitleId ?? generatedTitleId;
  const surfaceRef = useRef<HTMLDivElement>(null);
  // Save the element that had focus when the dialog opened so we can
  // restore focus to it on close. Without this, focus snaps to the
  // top of the document after dismissal.
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // Capture latest onClose into a ref so consumers can pass inline
  // arrows without re-tearing-down the keydown listener and focus
  // management every render.
  const onCloseRef = useLatestRef(onClose);

  // `auto` is a bottom sheet only on true phones; tablet and desktop get the
  // centered modal. A 1024–1180px iPad otherwise inherits the phone sheet and
  // a small dialog stretches edge-to-edge across the bottom.
  const effectiveVariant: "modal" | "sheet" =
    variant === "auto" ? (tier === "phone" ? "sheet" : "modal") : variant;

  // Body-scroll lock + ESC handler + focus management — all tied to
  // `open` so they engage and disengage cleanly per opening.
  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = (document.activeElement as HTMLElement | null) ?? null;

    // Scroll lock. iOS Safari (phone + iPad) IGNORES `overflow:hidden` for
    // touch-scroll — the page keeps scrolling behind the dialog. The only
    // reliable lock there is pinning the body with `position:fixed` while
    // preserving the scroll offset (restored on close). Desktop doesn't have
    // the iOS quirk and its app shell doesn't scroll the document, so plain
    // `overflow:hidden` is enough — keep `position:fixed` off desktop so the
    // frozen layout is untouched.
    const lockBodyFixed = tier !== "desktop";
    const lockedScrollY = window.scrollY;
    const lockBody = document.body;
    const prevLock = {
      bodyOverflow: lockBody.style.overflow,
      htmlOverflow: document.documentElement.style.overflow,
      position: lockBody.style.position,
      top: lockBody.style.top,
      left: lockBody.style.left,
      right: lockBody.style.right,
      width: lockBody.style.width,
    };
    lockBody.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    if (lockBodyFixed) {
      lockBody.style.position = "fixed";
      lockBody.style.top = `-${lockedScrollY}px`;
      lockBody.style.left = "0";
      lockBody.style.right = "0";
      lockBody.style.width = "100%";
    }

    const surface = surfaceRef.current;
    if (surface) focusFirstWithin(surface);

    function onKeyDown(event: KeyboardEvent) {
      if (closeOnEscape && event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (surface) trapTabKey(event, surface);
    }

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      lockBody.style.overflow = prevLock.bodyOverflow;
      document.documentElement.style.overflow = prevLock.htmlOverflow;
      if (lockBodyFixed) {
        lockBody.style.position = prevLock.position;
        lockBody.style.top = prevLock.top;
        lockBody.style.left = prevLock.left;
        lockBody.style.right = prevLock.right;
        lockBody.style.width = prevLock.width;
        // Restore the pre-lock scroll position (position:fixed reset it to 0).
        window.scrollTo(0, lockedScrollY);
      }
      const toRestore = previouslyFocused.current;
      // Use a microtask to dodge React's commit re-focusing whatever
      // it was about to focus next; we want the user's last focus back.
      if (toRestore && typeof toRestore.focus === "function") {
        queueMicrotask(() => toRestore.focus());
      }
    };
  }, [open, closeOnEscape, onCloseRef, tier]);

  const contextValue = useMemo<DialogContextValue>(
    () => ({ onClose, hideCloseButton, titleId }),
    [onClose, hideCloseButton, titleId],
  );

  if (!open) return null;

  const backdropClass =
    effectiveVariant === "sheet" ? "ui-dialog-backdrop sheet" : "ui-dialog-backdrop modal";
  const surfaceClass = ["ui-dialog-surface", effectiveVariant, className ?? ""].join(" ").trim();

  return createPortal(
    <DialogContext.Provider value={contextValue}>
      <div
        className={backdropClass}
        onClick={(e) => {
          // Only close if the user clicked the backdrop itself, not a
          // descendant. Clicks on the surface can also bubble up here.
          if (!closeOnBackdropClick) return;
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          ref={surfaceRef}
          className={surfaceClass}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </DialogContext.Provider>,
    document.body,
  );
}

function DialogHeader({ children, className }: { children: ReactNode; className?: string }) {
  const { t } = useTranslation("common");
  const { onClose, hideCloseButton, titleId } = useDialogContext();
  return (
    <header className={["ui-dialog-header", className ?? ""].join(" ").trim()}>
      <div id={titleId} className="ui-dialog-header-content">
        {children}
      </div>
      {!hideCloseButton && (
        <button
          type="button"
          className="ui-dialog-close"
          aria-label={t("chrome.closeDialog")}
          onClick={onClose}
        >
          <CloseCircle size={16} />
        </button>
      )}
    </header>
  );
}

function DialogBody({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <section className={["ui-dialog-body", className ?? ""].join(" ").trim()}>{children}</section>
  );
}

function DialogFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <footer className={["ui-dialog-footer", className ?? ""].join(" ").trim()}>{children}</footer>
  );
}

/**
 * Compose-style sub-components — `<Dialog.Header>`, `<Dialog.Body>`,
 * `<Dialog.Footer>`. Same ergonomics as the old GeneralDialog inner
 * components, but variant-agnostic.
 */
Dialog.Header = DialogHeader;
Dialog.Body = DialogBody;
Dialog.Footer = DialogFooter;

/** Hook for consumers that need access to the dialog's close callback. */
export function useDialogClose(): () => void {
  return useDialogContext().onClose;
}
